import {AssistantPendingPlan} from 'app/blocks/service/api/assistant.service';

/**
 * Plan → Approve flow of the HaulierMagic Assistant (CU-14zefhy0dgu; twin of the RouteMagic one).
 * A read turn may end with a proposed change (`pendingPlan` on the events response). The chat shows it as a card;
 * nothing is written until the user approves, which starts a separate write turn on the server.
 * Pure helpers live here so they can be unit tested without the component.
 */

/**
 * pending = waiting for the user · approving/cancelling = request in flight · expired = server no longer knows it (404)
 * · used = server says it was already approved or cancelled (409), e.g. from another tab or before a reload
 */
export type PlanCardState = 'pending' | 'approving' | 'approved' | 'cancelling' | 'cancelled' | 'expired' | 'used';

export interface PlanCard {
    id: string;
    summary: string;
    steps: string[];
    state: PlanCardState;
    /** epoch ms when the plan reached the browser (server keeps plans for PLAN_TTL_MS) */
    receivedAt: number;
}

/** Mirrors the server-side plan TTL (30 min); older pending cards are shown as expired without a round trip. */
export const PLAN_TTL_MS = 30 * 60 * 1000;

/** The fenced plan block the model writes (`rm-plan` per the shared contract; `hm-plan` accepted too). */
const PLAN_BLOCK = /```(?:rm|hm)-plan[^\n]*\n[\s\S]*?```/g;
const PLAN_FENCE = /```(?:rm|hm)-plan/;

/** Builds a card from the events response's `pendingPlan`; null when it is missing or has no id. */
export function toPlanCard(raw: AssistantPendingPlan | null | undefined, now: number = Date.now()): PlanCard | null {
    if (!raw || raw.id === undefined || raw.id === null || String(raw.id).trim() === '') {
        return null;
    }
    const steps = Array.isArray(raw.steps) ? raw.steps.filter((s) => s !== null && s !== undefined && String(s).trim() !== '').map(String) : [];
    return {id: String(raw.id), summary: (raw.summary || '').trim(), steps, state: 'pending', receivedAt: now};
}

/** Removes any fenced plan block from the reply text (the server strips it too; this guards older/raw results). */
export function stripPlanBlock(text: string): string {
    if (!text || !PLAN_FENCE.test(text)) {
        return text;
    }
    return text
        .replace(PLAN_BLOCK, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/** A pending card the server has certainly dropped (older than the TTL). */
export function isPlanExpired(plan: PlanCard, now: number = Date.now()): boolean {
    return plan.state === 'pending' && !!plan.receivedAt && now - plan.receivedAt > PLAN_TTL_MS;
}

/** Approve/Cancel buttons are usable only for a live pending card while no turn runs in that session. */
export function canActOnPlan(plan: PlanCard | null | undefined, sessionBusy: boolean, now: number = Date.now()): boolean {
    return !!plan && plan.state === 'pending' && !sessionBusy && !isPlanExpired(plan, now);
}

/** After a reload: in-flight states go back to pending (the server answers 404/409 if it was used), old ones expire. */
export function restorePlanCard(plan: PlanCard, now: number = Date.now()): PlanCard {
    let state = plan.state;
    if (state === 'approving' || state === 'cancelling') {
        state = 'pending';
    }
    const restored = {...plan, state};
    if (isPlanExpired(restored, now)) {
        restored.state = 'expired';
    }
    return restored;
}

/**
 * The server's own reason from an Approve/Cancel error body (HM answers RFC 7807 problem+json: the reason is in
 * `detail`; `message` there is an i18n key such as `error.http.409`, so it is used only when it reads like a sentence).
 * Empty string when there is none.
 */
export function planErrorDetail(error: any): string {
    const body = error?.error;
    if (typeof body === 'string') {
        return body.trim().startsWith('{') ? '' : body.trim();
    }
    const detail = typeof body?.detail === 'string' ? body.detail.trim() : '';
    if (detail) {
        return detail;
    }
    const message = typeof body?.message === 'string' ? body.message.trim() : '';
    return message && !/^error\.[\w.]+$/.test(message) ? message : '';
}

/**
 * Which final state an Approve/Cancel error puts the card in: 404 = unknown/expired (nothing was changed),
 * 409 = already used (the change may already have been made) unless the server's reason says the plan expired
 * (the HM API also answers 409 for a plan past its window, so nothing ran), anything else = null (retryable).
 */
export function planGoneState(httpStatus: number | undefined, serverDetail?: string): 'expired' | 'used' | null {
    if (httpStatus === 404) {
        return 'expired';
    }
    if (httpStatus === 409) {
        return /expire/i.test(serverDetail || '') ? 'expired' : 'used';
    }
    return null;
}

/**
 * Header status line and intro text. Plan mode (changes after approval) is shown only when the server says plans
 * are enabled for this tenant or a plan has actually arrived; otherwise today's read-only wording stays.
 */
export function assistantModeText(planMode: boolean): {status: string; intro: string} {
    return planMode
        ? {status: 'Online · changes need your approval', intro: "I can read, and I won't change anything without your approval."}
        : {status: 'Online · read-only', intro: "I can read, but I won't change anything."};
}

/** 404 (unknown/expired) and 409 (expired or already used) mean the plan is gone for good; anything else is retryable. */
export function isPlanGoneStatus(httpStatus: number | undefined): boolean {
    return planGoneState(httpStatus) !== null;
}

/** Short label shown on a card that can no longer be acted on. */
export function planStateLabel(plan: PlanCard, now: number = Date.now()): string {
    if (isPlanExpired(plan, now)) {
        return 'Expired — ask again to get a fresh plan';
    }
    switch (plan.state) {
        case 'approving':
            return 'Approving…';
        case 'approved':
            return 'Approved';
        case 'cancelling':
            return 'Cancelling…';
        case 'cancelled':
            return 'Cancelled';
        case 'expired':
            return 'No longer available — ask again to get a fresh plan';
        case 'used':
            return 'Already approved or cancelled — check the latest reply';
        default:
            return '';
    }
}
